import { useAuth } from '../lib/auth';

/**
 * What a signed-in free user sees. This client is Pro-only.
 *
 * They are signed in successfully and stay signed in: bouncing somebody who just typed a
 * correct password reads as a broken login, and this screen is an upgrade vector rather
 * than a dead end — the same reasoning as `PaywallView` on iOS.
 *
 * There is no purchase button. Pro is an in-app purchase through RevenueCat on the phone;
 * no web billing exists, so the honest instruction is to subscribe where the subscription
 * can actually be bought. No App Store link either — the app is on TestFlight and has no
 * public listing to link to yet.
 */
export default function Upgrade({ onRecheck }: { onRecheck: () => void }) {
  const { user, signOut } = useAuth();

  return (
    <main className="centre">
      <div className="card">
        <div className="brand brand-lg">
          <img src="/zsoogi-logo.png" alt="" width={40} height={40} />
          <h1>ZsoogiNotes</h1>
        </div>

        <p className="muted">
          The web app is part of <strong>Pro</strong>. Capture stays free on your phone —
          searching, reviewing and editing on a big screen is a Pro feature.
        </p>

        <p className="muted small">
          To subscribe, open ZsoogiNotes on your iPhone and tap your avatar → Account →
          Upgrade. Your notes are already here waiting; nothing needs importing.
        </p>

        {/* The subscription belongs to a Firebase uid, and a different sign-in method is a
            different uid even for the same person — signing in with Apple on the phone and
            Google here produces two accounts, one of which has paid. Naming the account
            that was just checked is the difference between "my subscription is broken" and
            "I am signed in as the wrong me". */}
        <p className="muted small">
          Checked against <strong>{user?.email ?? 'this account'}</strong>. If you subscribed
          using a different sign-in method, sign out and use that one.
        </p>

        <div className="actions">
          {/* Covers the common case: subscribed on the phone a moment ago, still looking at
              this screen. Better than telling somebody who has just paid to reload the page.
              No local "checking" state — the check moves App.tsx to `checking`, which
              unmounts this screen, so a spinner here would never be seen. */}
          <button type="button" className="primary" onClick={onRecheck}>
            I just subscribed — check again
          </button>
          <button type="button" className="danger" onClick={() => void signOut()}>
            Sign out
          </button>
        </div>
      </div>
    </main>
  );
}

/**
 * Shown when the entitlement check could not reach an answer.
 *
 * Deliberately NOT the upgrade screen. A network failure or a RevenueCat outage is not
 * evidence that the user is unsubscribed, and showing a subscriber a paywall because a
 * request timed out is the worse error. Retry is the only action; the message is shown
 * because an auth or entitlement failure that fails silently reads as the app hanging.
 */
export function EntitlementUnavailable({
  error,
  onRetry,
}: {
  error: string | null;
  onRetry: () => void;
}) {
  const { signOut } = useAuth();

  return (
    <main className="centre">
      <div className="card">
        <div className="brand brand-lg">
          <img src="/zsoogi-logo.png" alt="" width={40} height={40} />
          <h1>ZsoogiNotes</h1>
        </div>

        <p className="muted">
          We could not check your subscription just now, so we have not loaded your notes.
          This is us, not you — your account and your notes are unaffected.
        </p>

        {error && <p className="error small">{error}</p>}

        <div className="actions">
          <button type="button" className="primary" onClick={onRetry}>
            Try again
          </button>
          <button type="button" className="danger" onClick={() => void signOut()}>
            Sign out
          </button>
        </div>
      </div>
    </main>
  );
}
